import React from 'react';
import {Section, Table, NameCell, ValueCell, InputCell, EmptyCell} from '../components/cells';
import {money, money2, count, percent, multiple, rateLabel} from '../model/format';


const RANGE_ROWS = [
    {key: 'cap_rate', label: 'Cap Rate', format: percent},
    {key: 'price', label: 'Price', format: money},
    {key: 'price_per_unit', label: 'Price Per Unit', format: money},
    {key: 'psf', label: 'PSF', format: money2},
    {key: 'down_payment', label: 'Down Payment', format: money},
    {key: 'loan_amount', label: 'Loan Amount', format: money},
    {key: 'loan_payment', label: 'Loan Payment', format: money},
    {key: 'cash_flow', label: 'Cash Flow', format: money},
    {key: 'cash_on_cash', label: 'Cash on Cash', format: percent},
    {key: 'dscr', label: 'DSCR', format: multiple},
];

const VALUATION_ROWS = [
    {key: 'noi', label: 'Net Operating Income', format: money},
    {key: 'cap_rate', label: 'Cap Rate', format: percent},
    {key: 'price', label: 'Price', format: money, highlight: true},
    {key: 'price_per_unit', label: 'Price Per Unit', format: money},
    {key: 'psf', label: 'PSF', format: money2},
    {key: 'down_payment', label: 'Down Payment', format: money},
    {key: 'loan_amount', label: 'Loan Amount', format: money},
    {key: 'debt_service', label: 'Annual Debt Service', format: money},
    {key: 'cash_flow', label: 'Cash Flow', format: money},
    {key: 'cash_on_cash', label: 'Cash on Cash', format: percent},
    {key: 'dscr', label: 'DSCR', format: multiple},
];

/**
 * Pricing!B15:F33. Lines quoting a rate show it per column (`rate`), since current and
 * proforma rates differ.
 */
function incomeRows(income) {
    const rates = income.rates;
    return [
        {label: 'Gross Potential Rent', line: income.gross_potential_rent},
        {label: 'Less: Physical Vacancy', line: income.vacancy, rate: rates.vacancy},
        {label: 'Less: Economic Vacancy (NRU, LTL, RC, BD)', line: income.economic_vacancy, rate: rates.economic_vacancy},
        {label: 'Other Income - RUBS, Fees, Parking', line: income.other_income},
        {label: 'Total Gross Revenue', line: income.total_gross_revenue, strong: true},
        {label: 'Effective Gross Income', line: income.effective_gross_income, strong: true},
        {label: 'Operating Expenses', heading: true},
        ...income.expenses.map(e => ({
            label: e.label,
            line: e,
            indent: true,
            rate: e.key === 'management_fee' ? rates.management_fee : null,
        })),
        {label: 'Total Expenses', line: income.total_expenses, strong: true},
        {label: 'NET OPERATING INCOME', line: income.net_operating_income, total: true},
    ];
}

export default function Pricing({inputs, calc, setField}) {
    const input = (key, props = {}) => <InputCell value={inputs[key]} onChange={value => setField(key, value)} {...props}/>;
    const row = (label, cell) => <tr key={label}><NameCell>{label}</NameCell>{cell}</tr>;
    const anticipated = calc.pricingRange.findIndex(column => column.step === 0);

    const pricingAnalysis = (
        <Section title='Pricing Analysis' className='mf-pricing-analysis'>
            <Table className='mf-kv-table'>
                {row('Analysis Date', input('analysis_date', {kind: 'date'}))}
                {row('Cap Rate', input('cap_rate', {unit: '%'}))}
                {row('Down Payment', input('down_payment_percent', {unit: '%'}))}
                {row('Interest Rate', input('interest_rate', {unit: '%', decimals: 3}))}
                {row('Amortization (years)', input('amortization', {unit: 'Years'}))}
                {row('Total Units', <ValueCell>{count(calc.units)}</ValueCell>)}
                {row('Rentable SF', <ValueCell>{count(calc.rentableSf)}</ValueCell>)}
            </Table>
        </Section>
    );

    const pricingRange = (
        <Section title='Pricing Range' description='Current NOI'>
            <Table className='mf-range-table'>
                <tr className='mf-range-bps'>
                    <NameCell>BPS Change</NameCell>
                    {input('bps_change', {unit: '%'})}
                    {calc.pricingRange.slice(1).map((_, i) => <td key={i} className='invisible'/>)}
                </tr>
                {RANGE_ROWS.map(rangeRow => (
                    <tr key={rangeRow.key}>
                        <NameCell>{rangeRow.label}</NameCell>
                        {calc.pricingRange.map((column, index) => (
                            <ValueCell key={column.step} className={index === anticipated ? 'mf-highlight' : ''}>
                                {rangeRow.format(column[rangeRow.key])}
                            </ValueCell>
                        ))}
                    </tr>
                ))}
            </Table>
        </Section>
    );

    const incomeAndExpenses = (
        <Section title='Income & Expenses'>
            <Table className='mf-income-table mf-income-table--four' header={['', 'Current', 'Per Unit', 'Proforma', 'Per Unit']}>
                {incomeRows(calc.income).map(item => item.heading ?
                    <tr key={item.label} className='mf-heading-row'>
                        <NameCell>{item.label}</NameCell><EmptyCell/><EmptyCell/><EmptyCell/><EmptyCell/>
                    </tr>
                    :
                    <tr key={item.label} className={`${item.strong ? 'mf-strong-row' : ''} ${item.total ? 'mf-total-row' : ''}`}>
                        <NameCell className={item.indent ? 'mf-indent' : ''}>{item.label}</NameCell>
                        <ValueCell note={item.rate ? rateLabel(item.rate.current) : null}>{money(item.line.current)}</ValueCell>
                        <ValueCell>{money(item.line.current_per_unit)}</ValueCell>
                        <ValueCell note={item.rate ? rateLabel(item.rate.proforma) : null}>{money(item.line.proforma)}</ValueCell>
                        <ValueCell>{money(item.line.proforma_per_unit)}</ValueCell>
                    </tr>
                )}
            </Table>
        </Section>
    );

    const valuation = (
        <Section title='Valuation'>
            <Table className='mf-valuation-table' header={['', 'Current', 'Proforma']}>
                {VALUATION_ROWS.map(item => (
                    <tr key={item.key} className={item.highlight ? 'mf-price-row' : ''}>
                        <NameCell>{item.label}</NameCell>
                        <ValueCell className={item.highlight ? 'mf-highlight' : ''}>{item.format(calc.valuation.current[item.key])}</ValueCell>
                        <ValueCell className={item.highlight ? 'mf-highlight' : ''}>{item.format(calc.valuation.proforma[item.key])}</ValueCell>
                    </tr>
                ))}
            </Table>
        </Section>
    );

    // Two halves split by the dashed divider: inputs and income on the left, pricing outputs on the right.
    return (
        <div className='mf-tab mf-pricing'>
            <div className='Financials_list_columns mf-split'>
                <div className='Financials_list_column mf-split__half'>
                    {pricingAnalysis}
                    {incomeAndExpenses}
                </div>
                <div className='Financials_list_column mf-split__half'>
                    {pricingRange}
                    {valuation}
                </div>
            </div>
        </div>
    );
}
